import type { NotarialRequestListItem } from "@/services/notaryRequestsService";

/**
 * A row, plus where it sits in a parent/child group.
 *
 * `first` and `last` are carried rather than derived at render time because
 * both renderings need them — the table to draw the group's borders and the
 * card list to round only its outer corners.
 */
export interface GroupedProfessionalRow {
  row: NotarialRequestListItem;
  /** The RFQ title to draw above this row; set on the first member only. */
  groupTitle: string | null;
  /**
   * This row belongs to a source group — EXPLICIT, rather than inferred.
   *
   * A standalone row and the only child of a one-member group both answer
   * `first && last`, and they must render differently: the second sits under
   * a header and has to square its top edge against it. Reading that as
   * `!(first && last)` — the obvious thing to write — is wrong for exactly
   * the common case, since a professional usually works one discipline.
   */
  grouped: boolean;
  first: boolean;
  last: boolean;
  /**
   * How many rows the group holds — 1 for a standalone row.
   *
   * Carried so the header can say "2 trámites" without the renderer
   * re-walking the list to count its own siblings. It is the one number that
   * tells a professional at a glance whether the block below is the whole job
   * or just their half of it.
   */
  groupSize: number;
}

/**
 * Puts the trámites spawned from one RFQ together, under its title.
 *
 * WHY THIS IS NOT user-requests-page's groupChildrenUnderParents, which this
 * otherwise resembles: that one groups a parent ROW with the children beneath
 * it, and the PROFESSIONAL's list does not contain the parent. An RFQ is
 * listed to a professional only while its leg is quoting or awarded-unpaid,
 * and payment — the event that spawns the child — is exactly what removes it.
 * So there is no row to nest under, only the title the child carries, and
 * this draws a header rather than reordering rows around an existing one.
 *
 * A professional registered in BOTH disciplines holds two children of the same
 * RFQ, and those are the rows this exists for: without it they are two
 * identically-titled trámites sitting apart with nothing saying they are two
 * halves of one job. One child is the common case and still gains the line
 * saying which job it came from.
 *
 * GROUPED BY ID, NOT TITLE. Two consumers can both call a job "Constitución
 * de SAS"; keyed on a display string those merge into one group claiming four
 * trámites came from one job — wrong rather than absent, the worse failure.
 *
 * Order is otherwise preserved: a group takes the position of its FIRST
 * member, so the list stays sorted by whatever the server sorted it by, and
 * pagination is untouched because this is a pure re-ordering of one page.
 *
 * Twinned with groupBySourceRequest in apps/mobile-pros
 * (request-list-item.ts). Copied rather than extracted, like the rest of the
 * professional surfaces the two apps share — see the migration's design doc,
 * reversal 2.
 */
export function groupProfessionalRowsBySourceRequest(
  rows: readonly NotarialRequestListItem[],
): GroupedProfessionalRow[] {
  const bySource = new Map<string, NotarialRequestListItem[]>();
  for (const row of rows) {
    const sourceId = row.sourceRequestId;
    if (sourceId == null) continue;
    const bucket = bySource.get(sourceId);
    if (bucket) bucket.push(row);
    else bySource.set(sourceId, [row]);
  }

  const emitted = new Set<string>();
  const out: GroupedProfessionalRow[] = [];
  for (const row of rows) {
    const sourceId = row.sourceRequestId;
    if (sourceId == null) {
      out.push({
        row,
        groupTitle: null,
        grouped: false,
        first: true,
        last: true,
        groupSize: 1,
      });
      continue;
    }
    // The group is emitted whole at its first member's position; later
    // members are skipped rather than emitted a second time.
    if (emitted.has(sourceId)) continue;
    emitted.add(sourceId);
    const group = bySource.get(sourceId) ?? [row];
    group.forEach((member, index) => {
      out.push({
        row: member,
        // The header belongs to the group, not to each row in it.
        groupTitle: index === 0 ? member.sourceRequestTitle ?? null : null,
        grouped: true,
        first: index === 0,
        last: index === group.length - 1,
        groupSize: group.length,
      });
    });
  }
  return out;
}

/**
 * One visual block in the list: either a group of trámites spawned by one RFQ,
 * or a single standalone row.
 *
 * The table renders the flat `GroupedProfessionalRow[]` above, because a
 * <tbody> can only hold <tr>s and the block's edges have to be drawn with cell
 * borders. The MOBILE card list has no such constraint, and a block there is a
 * real element — one bordered, rounded container wrapping its header and its
 * children — which is the shape this exists to express.
 */
export type ProfessionalListBlock =
  | {
      kind: "group";
      /** Stable across renders: the RFQ the members came from. */
      key: string;
      title: string | null;
      rows: NotarialRequestListItem[];
    }
  | { kind: "single"; key: string; row: NotarialRequestListItem };

/**
 * The same grouping as above, expressed as nestable blocks.
 *
 * Built on the flat pass rather than beside it so there is exactly ONE answer
 * to "which rows came from one RFQ, and in what order" — a second independent
 * walk would be free to drift from the first, and the two renderings of the
 * same list would disagree about where a group starts.
 */
export function toProfessionalListBlocks(
  rows: readonly NotarialRequestListItem[],
): ProfessionalListBlock[] {
  const out: ProfessionalListBlock[] = [];
  for (const entry of groupProfessionalRowsBySourceRequest(rows)) {
    if (!entry.grouped) {
      out.push({ kind: "single", key: entry.row.id, row: entry.row });
      continue;
    }
    if (entry.first) {
      out.push({
        kind: "group",
        key: entry.row.sourceRequestId ?? entry.row.id,
        title: entry.groupTitle,
        rows: [entry.row],
      });
      continue;
    }
    const current = out[out.length - 1];
    // Unreachable while the flat pass emits a group's members consecutively
    // after its first — guarded rather than asserted so a future change to
    // that ordering degrades to a standalone row instead of throwing.
    if (current?.kind === "group") current.rows.push(entry.row);
    else out.push({ kind: "single", key: entry.row.id, row: entry.row });
  }
  return out;
}
